/**
 * Qualification de la preuve — chunk 9.AK.1.
 *
 * ## Trois notions à ne jamais confondre
 *
 *   `cell_status`  (9.AB.1) — que DIT ce champ ? offert, non offert, non détecté…
 *   `needs_review`          — faut-il aller VÉRIFIER cette valeur ?
 *   `consensus`    (9.AK.1) — sur QUOI cette valeur repose-t-elle ?
 *
 * Ce composant ne rend que la troisième. Une cellule peut être parfaitement
 * exploitable et reposer sur une seule lecture : c'est le cas de 99 cellules
 * mesurées, et c'est précisément ce que le conseiller ne pouvait pas voir avant.
 *
 * ## Ce qu'il ne fait pas
 *
 * **Il ne change aucun statut visuel métier.** Une cellule `single_model`
 * aujourd'hui exploitable garde exactement l'apparence qu'elle avait — décision PO :
 * le consensus qualifie en v1, il ne devient pas l'autorité. Faire virer 99 cellules
 * au rouge sans qu'aucune information nouvelle n'ait été produite dégraderait
 * l'écran sans rien apprendre à personne.
 *
 * **Aucun pourcentage de confiance.** « 93,72 % » aurait l'air d'une mesure ; ce
 * n'en est pas une. Les états sont des phrases que le conseiller peut lire et
 * recouper.
 *
 * **Rien de tout ceci ne va au client.** Le rapport 9.AI.1 n'en voit pas une ligne.
 */

import { useTranslation } from "react-i18next";

export type ConsensusInfo = {
  state: string;
  model_count: number;
  candidate_count: number;
};

/**
 * Teintes par état.
 *
 * Les accords sont **discrets** — vert franc ferait de la confirmation un
 * événement alors que c'est le cas nominal souhaité. Les états faibles sont
 * neutres et non alarmants : ils ne signalent pas une erreur, seulement une base
 * de preuve mince.
 */
const TEINTES: Record<string, string> = {
  exact_agreement: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  normalized_agreement: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  disagreement: "bg-amber-50 text-amber-800 ring-amber-200",
  single_candidate: "bg-slate-50 text-slate-600 ring-slate-200",
  single_model: "bg-slate-50 text-slate-600 ring-slate-200",
  not_comparable: "bg-slate-50 text-slate-600 ring-slate-200",
  invalid: "bg-red-50 text-red-700 ring-red-200",
  insufficient_confidence: "bg-slate-50 text-slate-600 ring-slate-200",
  absent: "bg-slate-50 text-slate-600 ring-slate-200",
};

export function ConsensusIndicator({ consensus }: { consensus: ConsensusInfo | null }) {
  const { t } = useTranslation();

  // `null` = pas encore qualifié, jamais « aucun consensus ». Afficher un état
  // pour une donnée antérieure au moteur ferait paraître défectueux un héritage
  // parfaitement sain.
  if (!consensus) return null;

  const teinte = TEINTES[consensus.state] ?? TEINTES.single_candidate!;
  const libelle = t(`consensus.state.${consensus.state}`, {
    defaultValue: t("consensus.state.unknown"),
  });

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${teinte}`}
      // Le détail au survol : combien de lectures, de combien de modèles. C'est la
      // réponse à « pourquoi cet état ? », et elle tient en une phrase.
      title={t("consensus.detail", {
        candidates: consensus.candidate_count,
        models: consensus.model_count,
      })}
    >
      {libelle}
    </span>
  );
}
